import { action, computed, makeObservable, observable, toJS } from 'mobx';

import SsoClient from '@teliaee/telia-sso-client';
import LanguageStore from '@stores/LanguageStore';
import RootStore from '@stores/RootStore';
import RouteStore from '@stores/RouteStore';
import {Auth, LoginOptions, Mandate, SsoResultModel, CustomerType} from '@config/Sso';
import config from '@config';
import API from "@util/ApiUtil";

const AUTH_TYPE_DEALER = 'dealerService';
const IVR_ROLE = 'role.distadmin.persons.full.access';

class SsoStore {
  @observable public loaded = false;
  @observable public auth: Auth;
  @observable public clientState: SsoResultModel = {};
  @observable public hardAuthRequired = true;
  @observable public isLoggingOut: boolean = false;
  @observable public isMandateSelect: boolean = false;

  private languageStore: LanguageStore;
  private routeStore: RouteStore;
  private readonly sso: SsoClient;

  public constructor(rootStore: RootStore) {
    makeObservable(this);
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;

    this.sso = SsoClient.connect(
      {
        onLogout: this.handleLogout,
        onLogin: this.handleLogin
      },
      {locale: this.languageStore.currentLanguage, authLevel: 2}
    );
    this.sso.loadInitialState();
  }

  @action
  public handleLogout = () => {
    this.isLoggingOut = true;
    this.clientState = { action: 'logout' };
    this.auth = null;
    window.location.reload();
  };
  
  @action
  private handleLogin = (ssoResult: SsoResultModel) => {
    this.clientState = ssoResult;
    this.auth = ssoResult.auth;

    if (!!this.dealer) {
      this.updateConfig();
    }

    if (this.loggedIn || !!this.dealer) {
      if (ssoResult.goto) {
        window.location.assign(ssoResult.goto);
        return;
      }
    }

    this.loaded = true;
  };

  private updateConfig() {
    return API.get('/settings/config')
        .then((response) => {
          config.initialize(response.data);
        })
        .catch(() => {});
  }

  @computed
  get customerId() {
    return !!this.auth && !!this.auth.customer && this.auth.customer.customerId;
  }

  @computed
  public get customerType() {
    return this.auth?.customer?.type as CustomerType;
  }

  @computed
  get person() {
    return !!this.auth && this.auth.person;
  }

  @computed
  get loggedIn() {
    return !!this.auth && !!this.auth.person;
  }

  @computed
  get loggedInWithHardAuth() {
    return this.loggedIn && this.auth.authLevel > 1;
  }

  @computed
  get dealer() {
    return this.auth && this.auth.dealer;
  }

  @computed
  get customer() {
    return this.auth && this.auth.customer;
  }

  @computed
  get hasLegalMandate() {
    return this.auth && this.auth.mandates && !!this.auth.mandates.find((authMandate: Mandate) => authMandate.mandate === 'LEGAL');
  }

  @computed
  get isSsoLoaded() {
    return !!this.ssoClientState;
  }

  @computed
  get loginOptions(): LoginOptions {
    return {
      selectMandate: true,
      locale: this.languageStore.currentLanguage,
      authLevel: this.hardAuthRequired && !this.isMandateSelect ? 2 : 0
    };
  }

  get ssoClientState() {
    return toJS(this.clientState);
  }

  @computed
  get ssoClient() {
    return this.sso;
  }

  @action
  public setIsMandateSelect(isMandateSelect: boolean) {
    this.isMandateSelect = isMandateSelect;
  }

  @computed
  get isPrivate(): boolean {
    return this.customer && this.customer.type === 'B2C';
  }

  @computed
  get isBusiness(): boolean {
    return this.customer && this.customer.type === 'B2B';
  }

  @computed
  get isDealer() {
    return this.auth && this.auth.authType === AUTH_TYPE_DEALER;
  }

  @computed
  get hasIvrRole() {
    return this.dealer && this.dealer.roles && this.dealer.roles.indexOf(IVR_ROLE) > -1;
  }

  @computed
  get personIsCustomer() {
    return this.person &&
      this.customer &&
      this.auth.person.personalCode === this.customer.registrationNumber &&
      this.auth.person.personalCodeCountryCode === this.customer.registrationNumberCountryCode;
  }
}

export default SsoStore;
